import { LitEntity, renderIf, html, css } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../../parcels/unmonitored-bin-profile';
import '../../common/bn-surface';
import '../../common/bn-card';
import './bin-profile';
import './child-bin-profiles';
import './bin-height-map';
import './bin-height-map-modal';
import '~/legacy-lit/parcels/bin-profile-ingredient';

class BinProfileIndex extends LitEntity {
  static get properties() {
    return {
      binLevelId: { type: String, attribute: false },
      publishedAt: { type: String, attribute: 'published-at' },
      millFeatures: { type: String, attribute: false },
      binOwner: { type: Object, attribute: false },
      noHeightMap: { type: Boolean },
      disableRotation: { type: Boolean, attribute: 'disable-rotation' },
      has3DImages: { type: Boolean, attribute: false },
      showModal: { type: Boolean },
      medicated: { type: Boolean },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        margin: 8px;
        display: block;
      }
      .bin-box {
        height: 200px;
        display: flex;
        margin: calc(var(--root-margin) / 2);
      }
      .height-map {
        height: 400px;
      }
      bn-surface {
        margin: calc(var(--root-margin) / 2);
      }
      child-bin-profiles {
        flex-grow: 1;
      }
      .small {
        display: flex;
        height: 50px;
        margin: calc(var(--root-margin) / 2);
      }
      .small .profile-container {
        margin-right: 6px;
        padding: 3px;
        border-bottom: 3px solid transparent;
        box-sizing: border-box;
      }
      .small .profile-container:hover {
        border-bottom: 3px solid var(--blue-60);
      }
      .small child-bin-profiles {
        flex-grow: 1;
      }
      .aggregate-total {
        box-sizing: border-box;
        display: inline-block;
        height: 100%;
        padding: 8px;
        background-color: var(--surface);
      }
      .aggregate-total bin-profile {
        width: 40px;
        stroke-width: 4px;
      }
      .small bin-profile {
        width: 40px;
      }
      .profile-container {
        display: inline-block;
        height: 50px;
        width: 50px;
      }
      bin-height-map {
        margin: calc(-1 *  var(--root-margin));
        height: calc(100% + 2 * var(--root-margin));
        width: calc(100% + 2 * var(--root-margin));
        --border-radius: 10px;
      }
      bn-card {
        height: 100%;
        width: 100%;
        --root-margin: 0;
      }
      bin-profile-ingredient[clickable] {
        cursor: pointer;
      }
      bin-profile[clickable] {
        cursor: pointer;
      }
    `];
  }

  updated(changedProperties) {
    if (changedProperties.has('href')) {
      this.noHeightMap = false;
      this.hasChildren = null;
      this.aggregateParentHref = null;
    }
  }

  render() {
    const isUnmonitored = ['inactive','unmonitored'].includes(this.entity?.properties?.monitoringType);
    const binOwnerHref = this.getHref('https://api.binsentry.com/rel/owner');
    const millHref = this.binOwner?.getLinkByRel('https://api.binsentry.com/rel/account')?.href;
    let renderType;
    if (this.hasChildren) {
      renderType = 'aggregate';
    } else if (this.entity?.properties.role === 'ingredient') {
      renderType = '3d';
    } else if (isUnmonitored) {
      renderType = 'unmonitored';
    } else if (!this.noHeightMap && this.entity?.properties?.dimensions?.shape === 'circular') {
      renderType = '3d';
    } else {
      renderType = '2d';
    }

    const supportModal = this.has3DImages;

    return html`
      ${renderIf(this.binLevelId, () => html`
        <lit-entity .href="${this.binLevelId}" @update="${this._onUpdateBinLevel}"></lit-entity>
      `)}

      ${renderIf(renderType === 'aggregate', () => html`
        <div class="bin-box">
          <div class="aggregate-total">
            <bin-profile
              .href="${this.href}"
              basic
              hideRings
              ?medicated="${this.medicated}"
            ></bin-profile>
          </div>
          <child-bin-profiles .href="${this.href}"></child-bin-profiles>
        </div>
      `)}

      ${renderIf(renderType === 'ingredient', () => html`
        <bin-profile-ingredient
          ?clickable="${supportModal}"
          @click="${() => this.showModal = supportModal}"
          .href="${this.href}"
        ></bin-profile-ingredient>
      `)}

      ${renderIf(renderType === 'unmonitored', () => html`
        <unmonitored-bin-profile .href="${this.href}"></unmonitored-bin-profile>
      `)}

      ${renderIf(renderType === '3d', () => html`
        <div class="bin-box height-map">
          <bn-card>
            <bin-height-map
              .href="${this.href}"
              bin-level-id="${this.binLevelId || ''}"
              ?auto-rotate="${!this.disableRotation}"
              auto-rotate-speed="${8}"
              ?medicated="${this.medicated}"
              @click="${() => this.showModal = true}"
              ?show-outlier-warning="${true}"
            ></bin-height-map>
          </bn-card>
        </div>
      `)}

      ${renderIf(renderType === '2d', () => html`
        <div class="bin-box">
          <bin-profile
            .href="${this.href}"
            ?clickable="${supportModal}"
            @click="${() => this.showModal = supportModal}"
            ?medicated="${this.medicated}"
          ></bin-profile>
        </div>
      `)}

      ${renderIf(supportModal, () => html`
        <bin-height-map-modal
          .href="${this.href}"
          ?hide="${!this.showModal}"
          @close="${() => this.showModal = false}"
        ></bin-height-map-modal>
      `)}

      ${renderIf(this.aggregateParentHref, () => this._renderSiblings())}

      <lit-entity .href="${binOwnerHref}" @update="${this._binOwnerChanged}"></lit-entity>
      <lit-entity .href="${millHref}" @update="${this._millChanged}"></lit-entity>
    `;
  }

  _binOwnerChanged(e) {
    const { entity } = e.detail || {};
    this.binOwner = entity;
  }

  _millChanged(e) {
    const { entity } = e.detail || {};
    this.millFeatures = entity?.properties.features;
  }

  _renderSiblings() {
    return html`
      <bn-surface class="small">
        <a class="profile-container" href="${`/bins/show/overview?binId=${encodeURIComponent(this.aggregateParentHref)}`}">
          <bin-profile .href="${this.aggregateParentHref}" hideRings basic></bin-profile>
        </a>
        <child-bin-profiles .href="${this.aggregateParentHref}" .selectedHref="${this.href}" small></child-bin-profiles>
      </bn-surface>
    `;
  }

  async _onUpdateBinLevel(e) {
    const { entity: binLevelEntity } = e.detail;
    this.noHeightMap = !binLevelEntity?.getSubEntityByRel('https://api.binsentry.com/rel/height-map');
    this.has3DImages = !!binLevelEntity?.getSubEntityByRel('https://api.binsentry.com/rel/3d-images');
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.hasChildren = !!this.getHref('https://api.binsentry.com/rel/bin-aggregate-child');
    this.aggregateParentHref = this.getHref('https://api.binsentry.com/rel/bin-aggregate-parent');
    this.binLevelId = this.publishedAt ? `${this.getHref('self')}/levels/${this.publishedAt}` : this.getHref('https://api.binsentry.com/rel/bin-level-latest');
  }
}

window.customElements.define('bin-profile-index', BinProfileIndex);
